import { useSEO } from "@/hooks/useSEO";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MapPin, Facebook, MessageCircle, Star } from "lucide-react";

export default function Contact() {
  useSEO({ title: "Airport Car Rental Nassau — Contact Us | Easy Drive", description: "Reach Easy Drive Car Rental in Nassau: WhatsApp +1 (242) 813-1775, email bookings@easydrivecarrental.com, or visit us on West Bay Street.", path: "/contact" });
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent — we'll reply shortly");
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="max-w-6xl mx-auto px-6 lg:px-10 pt-32 pb-24">
      <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-primary">Get in touch</p>
      <h1 className="mt-3 font-display text-5xl sm:text-6xl tracking-tight">
        Contact <span className="italic gold-text">us</span>
      </h1>
      <p className="mt-5 text-muted-foreground max-w-xl leading-relaxed">
        Questions about a booking, the fleet, or policies? We answer fast — WhatsApp
        is usually the quickest way to reach us.
      </p>

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="space-y-5">
          <div className="bg-card border border-border rounded-2xl p-6">
            <p className="font-ui text-xs tracking-[0.2em] uppercase text-muted-foreground">Fastest reply</p>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              Message us on WhatsApp — we typically reply in minutes, 7 days a week.
            </p>
            <a data-testid="contact-whatsapp-link" href="https://wa.me/12428131775" target="_blank" rel="noopener noreferrer">
              <Button className="mt-4 rounded-full bg-[#25D366] text-white hover:opacity-90 font-ui">
                <MessageCircle size={15} className="mr-2" /> Chat on WhatsApp
              </Button>
            </a>
          </div>
          {[
            { icon: Facebook, t: "Facebook", d: "Follow Easy Drive Car Rental", href: "https://facebook.com/easydrivecarrental242", testid: "contact-facebook-link" },
            { icon: Star, t: "Google Reviews", d: "Read & leave a review", href: "https://www.google.com/maps/search/Easy+Drive+Car+Rental+Nassau+Bahamas", testid: "contact-google-reviews-link" },
            { icon: MapPin, t: "Office", d: "West Bay Street, Nassau, Bahamas", href: null, testid: "contact-address" },
          ].map(({ icon: Icon, t, d, href, testid }) => (
            <div key={testid} className="flex items-center gap-4 bg-card border border-border rounded-2xl p-5">
              <div className="w-10 h-10 rounded-full border border-primary/30 flex items-center justify-center shrink-0">
                <Icon size={16} className="gold-text" />
              </div>
              <div>
                <p className="font-ui text-xs tracking-[0.2em] uppercase text-muted-foreground">{t}</p>
                {href ? (
                  <a data-testid={testid} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="text-sm font-ui text-foreground hover:text-primary transition-colors duration-200">
                    {d}
                  </a>
                ) : (
                  <p data-testid={testid} className="text-sm font-ui text-foreground">{d}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-card border border-border rounded-2xl p-8">
          {sent ? (
            <div data-testid="contact-success-message" className="h-full flex flex-col items-start justify-center">
              <p className="font-display text-3xl tracking-tight">Message <span className="italic gold-text">received</span></p>
              <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                Thanks for reaching out — we typically reply within a couple of hours.
              </p>
              <Link to="/fleet" className="mt-8">
                <Button className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui">Browse the Fleet</Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div>
                <Label className="font-ui text-xs tracking-wide text-muted-foreground">Name</Label>
                <Input data-testid="contact-name-input" required value={form.name} onChange={set("name")} className="input-dark mt-2" />
              </div>
              <div>
                <Label className="font-ui text-xs tracking-wide text-muted-foreground">Email</Label>
                <Input data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} className="input-dark mt-2" />
              </div>
              <div>
                <Label className="font-ui text-xs tracking-wide text-muted-foreground">Phone / WhatsApp (optional)</Label>
                <Input data-testid="contact-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="+1 (242) 000-0000" className="input-dark mt-2" />
              </div>
              <div>
                <Label className="font-ui text-xs tracking-wide text-muted-foreground">Message</Label>
                <Textarea data-testid="contact-message-input" required rows={5} value={form.message} onChange={set("message")} placeholder="How can we help?" className="input-dark mt-2" />
              </div>
              <Button data-testid="contact-submit-button" disabled={sending} className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui h-11">
                {sending ? "Sending…" : "Send Message"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
